import { useParams, Link, Navigate } from "react-router-dom";
import { Target, Wrench, TrendingUp, Quote, CheckCircle2 } from "lucide-react";
import { getCollateral } from "../../lib/collateral";
import { COMPANY, IMAGES } from "../../lib/data";
import { B, CollateralBar } from "../../components/site/CollateralKit";

export default function ProductCaseStudy() {
  const { product } = useParams();
  const c = getCollateral(product);
  if (!c) return <Navigate to="/solutions" replace />;
  const cs = c.caseStudy;
  const pdf = `/${c.slug}-case-study.pdf`;
  const cols = [
    { icon: Target, title: "The Challenge", body: cs.challenge },
    { icon: Wrench, title: "The Deployment", body: cs.deployment },
    { icon: TrendingUp, title: "The Outcome", body: cs.outcome },
  ];

  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <CollateralBar product={c.slug} label="Case Study · One-Page" pdf={pdf} testid="case-study" />
      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="collateral-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">{c.name} · Project Profile</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[29px]">{cs.headline}</h1>
              <p className="mt-2 text-charcoal text-[11px]">{cs.sector}</p>
            </div>
            <img src={c.image} alt={c.name} className="w-40 h-28 object-contain bg-white border border-border p-1" />
          </header>

          <div className="flex-1 grid grid-cols-3 gap-4 pt-4 min-h-0">
            {cols.map((col) => (
              <div key={col.title} className="border border-border flex flex-col">
                <div className="bg-ink text-white px-4 py-2 flex items-center gap-2"><col.icon className="h-4 w-4 text-flame" /><span className="font-display font-bold text-[12px] uppercase tracking-wider">{col.title}</span></div>
                <ul className="p-4 space-y-2.5 flex-1 flex flex-col justify-center">
                  {col.body.map((b) => (<li key={b} className="flex items-start gap-2 text-charcoal text-[11px] leading-snug"><CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" /><span><B t={b} /></span></li>))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">Measured Results</div>
            <div className="mt-2 grid grid-cols-4 gap-3">
              {cs.results.map((o) => (<div key={o.l} className="bg-white/5 border border-white/10 px-4 py-3"><div className="font-display font-extrabold text-flame text-[24px] leading-none">{o.v}</div><div className="text-white/60 text-[9.5px] mt-1">{o.l}</div></div>))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div className="flex items-start gap-2 max-w-[62%]"><Quote className="h-5 w-5 text-flame flex-shrink-0" /><p className="italic text-charcoal text-[11px] leading-snug">"{cs.quote}"<span className="not-italic text-muted-foreground"> — Field lead (representative)</span></p></div>
            <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">Request a Project Analysis</Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Representative project profile for evaluation; figures are illustrative estimates. Not a quote, guarantee, or warranty of any specific result; not financial, engineering, or legal advice. Actual outcomes vary by site conditions &amp; usage. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
